import { type MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Apple, Smartphone, Globe, Copy, Check, X, ArrowUpRight, ExternalLink, Info } from 'lucide-react';

const IOS_URL = 'https://apps.apple.com/cn/app/id6755644246';
const ANDROID_CN_URL = 'https://file.market.xiaomi.com/download/AppStore/00a01ed9467164eaeaa2cfcc0471fc1245dc3d6ec/com.yelizibg_1.2.1.apk';
const ANDROID_GLOBAL_URL = 'https://play.google.com/store/apps/details?id=com.yelizibg';
const WEB_URL = 'http://qnbg-web.suntable.cn/home';

/** 跳转被拦截后，多久判定为「没有跳转」（毫秒） */
const REDIRECT_CHECK_DELAY = 2500;

/** 识别会拦截下载/外链跳转的内置浏览器环境 */
function detectBlockedEnv(): 'wechat' | 'inapp' | 'normal' {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('micromessenger') || ua.includes('weixin') || ua.includes('wechat')) {
        return 'wechat';
    }
    if (
        ua.includes('weibo') ||
        ua.includes('qq/') ||
        ua.includes(' qq') ||
        ua.includes('dingtalk') ||
        ua.includes('alipayclient') ||
        ua.includes('baiduboxapp') ||
        ua.includes('quark') ||
        ua.includes('douban') ||
        ua.includes('douyin') ||
        ua.includes('toutiao')
    ) {
        return 'inapp';
    }
    return 'normal';
}

/** 引导弹层文案（简体，与 qnbg/download.tsx 一致） */
const T = {
    iosBtn: 'App Store 下载',
    iosSub: 'iPhone · iPad',
    androidBtn: '安卓下载',
    androidSub: 'Android',
    webBtn: '网页版',
    webSub: '在线使用',
    wxTip: '当前在微信中打开，下载链接可能被限制',
    guideWxTitle: '请点击右上角，在浏览器中打开',
    guideWxDesc: '微信内置浏览器不支持直接下载安装，请按以下步骤操作：',
    guideWxStep1: '点击右上角的「···」按钮',
    guideWxStep2: '选择「在浏览器打开」',
    guideWxStep3: '在浏览器中再次点击下载即可',
    guideWxHint: '也可以复制链接后，粘贴到系统浏览器（Safari / Chrome）中打开',
    guideFallbackTitle: '下载没有自动开始？',
    guideFallbackDesc: '当前浏览器可能限制了跳转或下载，建议复制链接后在系统浏览器（Safari / Chrome）中打开。',
    guideFallbackHint: '如果已经开始下载，直接关闭本提示即可。',
    guideInappTitle: '请在浏览器中打开',
    guideInappDesc: '当前应用内置浏览器可能拦截了下载，请点击右上角菜单选择「用浏览器打开」，或复制链接后到浏览器中下载。',
    copyLink: '复制链接地址',
    copied: '已复制',
    copyFail: '复制失败，请长按链接手动复制',
    retryOpen: '继续尝试打开',
    close: '关闭',
};

export default function DownloadEntry({ floating = false }: { floating?: boolean }) {
    const env = useMemo(() => detectBlockedEnv(), []);

    /** 引导弹层：wechat=微信蒙层引导，inapp=其它内置浏览器，fallback=普通浏览器未跳转 */
    const [guide, setGuide] = useState<{ type: 'wechat' | 'inapp' | 'fallback'; url: string } | null>(null);
    const [copyState, setCopyState] = useState<'idle' | 'ok' | 'fail'>('idle');
    const timerRef = useRef<number | null>(null);

    useEffect(() => () => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
    }, []);

    /* 弹层打开时锁定页面滚动，并支持 ESC 关闭 */
    useEffect(() => {
        if (!guide) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setGuide(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [guide]);

    /* 复制链接（微信等环境下 Clipboard API 不可用，降级 execCommand） */
    const copyLink = async (url: string) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(url);
            } else {
                const input = document.createElement('textarea');
                input.value = url;
                input.setAttribute('readonly', '');
                input.style.position = 'fixed';
                input.style.top = '-1000px';
                input.style.opacity = '0';
                document.body.appendChild(input);
                input.select();
                input.setSelectionRange(0, url.length);
                const ok = document.execCommand('copy');
                document.body.removeChild(input);
                if (!ok) throw new Error('copy failed');
            }
            setCopyState('ok');
        } catch (e) {
            setCopyState('fail');
        }
        window.setTimeout(() => setCopyState('idle'), 2500);
    };

    /* 点击下载：微信/内置浏览器直接引导；普通浏览器先跳转，未跳转再引导 */
    const handleDownload = (e: ReactMouseEvent<HTMLAnchorElement>, url: string) => {
        if (env === 'wechat' || env === 'inapp') {
            e.preventDefault();
            setGuide({ type: env, url });
            return;
        }
        e.preventDefault();
        // 网页版只是打开站点，不需要「下载未开始」兜底引导
        if (url === WEB_URL) {
            const w = window.open(url, '_blank');
            if (!w) window.location.href = url;
            return;
        }
        const newWindow = window.open(url, '_blank');
        if (!newWindow) {
            // 弹出被拦截：直接在当前页跳转；若仍停留在当前页，再提示引导
            window.location.href = url;
            if (timerRef.current) window.clearTimeout(timerRef.current);
            timerRef.current = window.setTimeout(() => {
                // 页面仍处于前台（未隐藏且仍有焦点）说明跳转/下载没有发生
                if (!document.hidden && document.hasFocus()) {
                    setGuide({ type: 'fallback', url });
                }
            }, REDIRECT_CHECK_DELAY);
        }
    };

    const isWechatGuide = guide?.type === 'wechat';

    return (
        <>
            <div className={`qbg-dl-buttons${floating ? ' qbg-dl-float' : ''}`}>
                <a
                    className="qbg-dl-btn qbg-dl-btn-ios"
                    href={IOS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => handleDownload(e, IOS_URL)}
                >
                    <Apple size={22} />
                    <span>
                        {T.iosBtn}
                        <em className="qbg-dl-btn-sub">{T.iosSub}</em>
                    </span>
                </a>
                <a
                    className="qbg-dl-btn qbg-dl-btn-android"
                    href={ANDROID_CN_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => handleDownload(e, ANDROID_CN_URL)}
                >
                    <Smartphone size={22} />
                    <span>
                        {T.androidBtn}
                        <em className="qbg-dl-btn-sub">{T.androidSub}</em>
                    </span>
                </a>
                <a
                    className="qbg-dl-btn qbg-dl-btn-web"
                    href={WEB_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => handleDownload(e, WEB_URL)}
                >
                    <Globe size={22} />
                    <span>
                        {T.webBtn}
                        <em className="qbg-dl-btn-sub">{T.webSub}</em>
                    </span>
                </a>
                {env === 'wechat' && (
                    <p className="qbg-dl-wxtip">
                        <Info size={14} />
                        {T.wxTip}
                    </p>
                )}
            </div>

            {/* 微信内置浏览器：右上角引导蒙层 */}
            {isWechatGuide && guide && (
                <div className="qbg-guide qbg-guide-wechat" onClick={() => setGuide(null)}>
                    <div className="qbg-guide-arrow">
                        <ArrowUpRight size={40} strokeWidth={2.5} />
                        <span>{T.guideWxStep1}</span>
                    </div>
                    <div className="qbg-guide-card" onClick={(e) => e.stopPropagation()}>
                        <h3 className="qbg-guide-title">{T.guideWxTitle}</h3>
                        <p className="qbg-guide-desc">{T.guideWxDesc}</p>
                        <ol className="qbg-guide-steps">
                            <li>{T.guideWxStep1}</li>
                            <li>{T.guideWxStep2}</li>
                            <li>{T.guideWxStep3}</li>
                        </ol>
                        <input
                            className="qbg-guide-url"
                            readOnly
                            value={guide.url}
                            onFocus={(e) => e.currentTarget.select()}
                            onClick={(e) => e.currentTarget.select()}
                        />
                        <button className="qbg-guide-btn" onClick={() => copyLink(guide.url)}>
                            {copyState === 'ok' ? <Check size={18} /> : <Copy size={18} />}
                            {copyState === 'ok' ? T.copied : T.copyLink}
                        </button>
                        <p className="qbg-guide-hint">
                            {copyState === 'fail' ? T.copyFail : T.guideWxHint}
                        </p>
                    </div>
                </div>
            )}

            {/* 其它 App 内置浏览器 / 普通浏览器未跳转：底部弹窗引导 */}
            {guide && guide.type !== 'wechat' && (
                <div className="qbg-guide qbg-guide-sheet" onClick={() => setGuide(null)}>
                    <div className="qbg-guide-panel" onClick={(e) => e.stopPropagation()}>
                        <span className="qbg-guide-bar" />
                        <button className="qbg-guide-close" onClick={() => setGuide(null)} aria-label={T.close}>
                            <X size={20} />
                        </button>
                        <h3 className="qbg-guide-title">
                            {guide.type === 'inapp' ? T.guideInappTitle : T.guideFallbackTitle}
                        </h3>
                        <p className="qbg-guide-desc">
                            {guide.type === 'inapp' ? T.guideInappDesc : T.guideFallbackDesc}
                        </p>
                        <input
                            className="qbg-guide-url"
                            readOnly
                            value={guide.url}
                            onFocus={(e) => e.currentTarget.select()}
                            onClick={(e) => e.currentTarget.select()}
                        />
                        <div className="qbg-guide-actions">
                            <button className="qbg-guide-btn" onClick={() => copyLink(guide.url)}>
                                {copyState === 'ok' ? <Check size={18} /> : <Copy size={18} />}
                                {copyState === 'ok' ? T.copied : T.copyLink}
                            </button>
                            <a
                                className="qbg-guide-btn qbg-guide-btn-ghost"
                                href={guide.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setGuide(null)}
                            >
                                <ExternalLink size={18} />
                                {T.retryOpen}
                            </a>
                        </div>
                        <p className="qbg-guide-hint">
                            {copyState === 'fail' ? T.copyFail : T.guideFallbackHint}
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}
